if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];
      previousPriceMarkup = undefined;

      constructor() {
        super();

        this.quantityInput = this.querySelector('.quantity__input');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.addEventListener('click', this.handleClearOptionsClick.bind(this));
        this.initQuantityHandlers();
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'))
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;
        if (!this.hasCompleteOptionSelection()) return;

        this.resetProductFormState();
        this.showPriceLoading();

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
        });
      }

      handleClearOptionsClick(event) {
        const clearOptionsLink = event.target.closest('[data-clear-options]');
        if (!clearOptionsLink || !this.contains(clearOptionsLink)) return;

        event.preventDefault();

        const monogramInput = this.querySelector('#monogram-build');
        const monogramPreviewFileInput = this.querySelector('[data-monogram-preview-file]');
        const monogramPreviewImage = this.querySelector('[data-monogram-preview-image]');
        if (monogramInput) {
          monogramInput.value = '';
          const storageKey = monogramInput.dataset.storageKey;
          if (storageKey) sessionStorage.removeItem(storageKey);
        }
        if (monogramPreviewFileInput) {
          monogramPreviewFileInput.value = '';
          if (monogramPreviewFileInput.form) {
            monogramPreviewFileInput.form.__mrkMonogramPreviewFilePromise = Promise.resolve();
          }
        }
        if (monogramPreviewImage) {
          const previewBaseUrl = monogramPreviewImage.dataset.previewBaseUrl;
          const fallbackValue = monogramPreviewImage.dataset.previewFallback || 'MRK';
          const foreground = monogramPreviewImage.dataset.previewForeground || '#582d40';
          const background = monogramPreviewImage.dataset.previewBackground || '#ffffff';

          if (previewBaseUrl) {
            monogramPreviewImage.src =
              `${previewBaseUrl}/${encodeURIComponent(fallbackValue)}.svg?foreground=${encodeURIComponent(foreground)}&background=${encodeURIComponent(background)}`;
          }
          monogramPreviewImage.alt = 'Monogram layout preview';
        }

        this.resetProductFormState();
        this.showPriceLoading();
        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(this.dataset.url, []),
          callback: this.handleResetProductInfo(this.dataset.url),
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
          })
          .then(() => {
            // set focus to last clicked option value
            if (targetId) document.querySelector(`#${targetId}`)?.focus();
            this.clearPriceLoading();
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            } else {
              console.error(error);
            }
            this.clearPriceLoading();
          });
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          const currentSelects = Array.from(this.variantSelectors.querySelectorAll('select'));
          const updatedSelects = Array.from(variantSelects.querySelectorAll('select'));

          currentSelects.forEach((currentSelect, index) => {
            const currentSelectedOption = currentSelect.selectedOptions[0];
            const updatedSelect = updatedSelects[index];

            if (!currentSelectedOption?.classList.contains('mrk-unchosen') || !updatedSelect) return;

            updatedSelect.querySelector('option[selected]')?.removeAttribute('selected');

            let placeholderOption = updatedSelect.querySelector('option.mrk-unchosen');
            if (!placeholderOption) {
              placeholderOption = currentSelectedOption.cloneNode(true);
              updatedSelect.prepend(placeholderOption);
            }

            placeholderOption.selected = true;
            placeholderOption.setAttribute('selected', 'selected');
            updatedSelect.value = '';
          });

          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
          requestAnimationFrame(() => window.mrkUpdateOptionAvailability?.(true));
        }
      }

      updateProductInfoContent(html, variant) {
        const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
          const source = html.getElementById(`${id}-${this.sectionId}`);
          const destination = this.querySelector(`#${id}-${this.dataset.section}`);
          if (source && destination) {
            destination.innerHTML = source.innerHTML;
            destination.classList.toggle('hidden', shouldHide(source));
          }
        };

        updateSourceFromDestination('price');
        updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
        updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
        updateSourceFromDestination('Volume');
        updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));
        updateSourceFromDestination('RewardsPoints', ({ innerText }) => innerText.trim() === '');
        updateSourceFromDestination('AffirmWidget', ({ innerText, children }) => innerText.trim() === '' && children.length === 0);
        updateSourceFromDestination('PreOrderMessage');
        updateSourceFromDestination('PaymentButton');
        updateSourceFromDestination('QuantitySelector', ({ innerText }) => innerText.trim() === '');
        updateSourceFromDestination('SetOptions');

        const sourceSku = html.querySelector('.product-info-sku');
        const skuTargets = (this.closest('.mrk-product') || document).querySelectorAll('.product-info-sku');
        if (sourceSku && skuTargets.length) {
          skuTargets.forEach((target) => {
            target.innerHTML = sourceSku.innerHTML;
          });
        }

        if (variant) {
          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');
        } else {
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.add('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.add('hidden');
        }

        const sourceSubmitButton = html.getElementById(`ProductSubmitButton-${this.sectionId}`);
        const destinationSubmitButton = this.querySelector(`#ProductSubmitButton-${this.dataset.section}`);
        const sourceSubmitButtonText = sourceSubmitButton?.querySelector('span')?.textContent?.trim();
        const sourceSubmitButtonDisabled =
          sourceSubmitButton?.hasAttribute('disabled') || sourceSubmitButton?.classList.contains('disabled');

        if (sourceSubmitButton && destinationSubmitButton) {
          destinationSubmitButton.className = sourceSubmitButton.className;
          destinationSubmitButton.title = sourceSubmitButton.title;
          destinationSubmitButton.querySelector('span').innerHTML = sourceSubmitButton.querySelector('span').innerHTML;
        }

        this.productForm?.toggleSubmitButton(sourceSubmitButtonDisabled ?? true, sourceSubmitButtonText);
        window?.Shopify?.PaymentButton?.init();
        window?.affirm?.ui?.refresh?.();
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const variant = this.getSelectedVariant(html);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            this.setUnavailable();
            return;
          }

          this.updateMedia(html, variant?.featured_media?.id);
          this.updateProductInfoContent(html, variant);

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      handleResetProductInfo(productUrl) {
        return (html) => {
          this.updateOptionValues(html);
          this.updateCustomGalleryMedia(html);
          this.updateURL(productUrl, null);
          this.updateVariantInputs(null);
          this.updateProductInfoContent(html, null);
          requestAnimationFrame(() => window.mrkResetOptionAvailability?.());
        };
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        const productUrl = new URL(url, window.location.origin);

        if (variantId) {
          productUrl.searchParams.set('variant', variantId);
        } else {
          productUrl.searchParams.delete('variant');
        }

        this.querySelector('share-button')?.updateUrl(productUrl.href);

        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState(
          {},
          '',
          `${productUrl.pathname}${productUrl.search}${productUrl.hash}`
        );
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);

        const selectors = ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      showPriceLoading() {
        const priceContainer = this.querySelector(`#price-${this.dataset.section}`);
        if (!priceContainer) return;

        this.previousPriceMarkup = priceContainer.innerHTML;
        priceContainer.dataset.loading = 'true';
        priceContainer.innerHTML = '<span class="price">loading...</span>';
      }

      clearPriceLoading() {
        const priceContainer = this.querySelector(`#price-${this.dataset.section}`);
        if (!priceContainer || !priceContainer.dataset.loading) return;

        if (priceContainer.textContent.trim() === 'loading...' && this.previousPriceMarkup) {
          priceContainer.innerHTML = this.previousPriceMarkup;
        }

        delete priceContainer.dataset.loading;
        this.previousPriceMarkup = undefined;
      }

      updateCustomGalleryMedia(html) {
        const sourcePrimarySlide = html.querySelector('.product-image .swiper-slide');
        const destinationPrimarySlide = this.querySelector('.product-image .swiper-slide');
        const sourcePrimarySlideImage = sourcePrimarySlide?.querySelector('img');
        const destinationPrimarySlideImage = destinationPrimarySlide?.querySelector('img');

        if (sourcePrimarySlide && destinationPrimarySlide) {
          const sourceMediaId = sourcePrimarySlide.getAttribute('data-media');
          if (sourceMediaId) {
            destinationPrimarySlide.setAttribute('data-media', sourceMediaId);
          }
        }

        if (sourcePrimarySlideImage && destinationPrimarySlideImage) {
          destinationPrimarySlideImage.replaceWith(sourcePrimarySlideImage.cloneNode(true));
        }

        const sourcePrimaryThumb = html.querySelector('.mrk-thumbs .mrk-thumbs__img');
        const destinationPrimaryThumb = this.querySelector('.mrk-thumbs .mrk-thumbs__img');
        const sourcePrimaryThumbImage = sourcePrimaryThumb?.querySelector('img');
        const destinationPrimaryThumbImage = destinationPrimaryThumb?.querySelector('img');

        if (sourcePrimaryThumb && destinationPrimaryThumb) {
          const sourceThumbId = sourcePrimaryThumb.getAttribute('data-id');
          if (sourceThumbId) {
            destinationPrimaryThumb.setAttribute('data-id', sourceThumbId);
          }
        }

        if (sourcePrimaryThumbImage && destinationPrimaryThumbImage) {
          destinationPrimaryThumbImage.replaceWith(sourcePrimaryThumbImage.cloneNode(true));
        }
      }

      updateMedia(html, variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return;

        this.updateCustomGalleryMedia(html);

        const mediaGallerySource = this.querySelector('media-gallery ul');
        const mediaGalleryDestination = html.querySelector(`media-gallery ul`);

        const refreshSourceData = () => {
          if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);
          const mediaGallerySourceItems = Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]'));
          const sourceSet = new Set(mediaGallerySourceItems.map((item) => item.dataset.mediaId));
          const sourceMap = new Map(
            mediaGallerySourceItems.map((item, index) => [item.dataset.mediaId, { item, index }])
          );
          return [mediaGallerySourceItems, sourceSet, sourceMap];
        };

        if (mediaGallerySource && mediaGalleryDestination) {
          let [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
          const mediaGalleryDestinationItems = Array.from(
            mediaGalleryDestination.querySelectorAll('li[data-media-id]')
          );
          const destinationSet = new Set(mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId));
          let shouldRefresh = false;

          // add items from new data not present in DOM
          for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
            if (!sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)) {
              mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
              shouldRefresh = true;
            }
          }

          // remove items from DOM not present in new data
          for (let i = 0; i < mediaGallerySourceItems.length; i++) {
            if (!destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)) {
              mediaGallerySourceItems[i].remove();
              shouldRefresh = true;
            }
          }

          // refresh
          if (shouldRefresh) [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();

          // if media galleries don't match, sort to match new data order
          mediaGalleryDestinationItems.forEach((destinationItem, destinationIndex) => {
            const sourceData = sourceMap.get(destinationItem.dataset.mediaId);

            if (sourceData && sourceData.index !== destinationIndex) {
              mediaGallerySource.insertBefore(
                sourceData.item,
                mediaGallerySource.querySelector(`li:nth-of-type(${destinationIndex + 1})`)
              );

              // refresh source now that it has been modified
              [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
            }
          });
        }

        // set featured media as active in the media gallery
        this.querySelector(`media-gallery`)?.setActiveMedia?.(
          `${this.dataset.section}-${variantFeaturedMediaId}`,
          true
        );

        // update media modal
        const modalContent = this.productModal?.querySelector(`.product-media-modal__content`);
        const newModalContent = html.querySelector(`product-modal .product-media-modal__content`);
        if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId || !this.quantityForm) return;

        this.querySelector('.quantity__rules-cart .loading__spinner')?.classList.remove('hidden');
        return fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() => this.querySelector('.quantity__rules-cart .loading__spinner')?.classList.add('hidden'));
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput || !this.quantityForm) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        if (!quantityFormUpdated) return;
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
            if (selector === '.quantity__label') {
              const updatedAriaLabelledBy = updated.getAttribute('aria-labelledby');
              if (updatedAriaLabelledBy) {
                current.setAttribute('aria-labelledby', updatedAriaLabelledBy);
                // Update the referenced visually hidden element
                const labelId = updatedAriaLabelledBy;
                const currentHiddenLabel = document.getElementById(labelId);
                const updatedHiddenLabel = html.getElementById(labelId);
                if (currentHiddenLabel && updatedHiddenLabel) {
                  currentHiddenLabel.textContent = updatedHiddenLabel.textContent;
                }
              }
            }
          }
        }
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      hasCompleteOptionSelection() {
        const selectInputs = Array.from(this.variantSelectors?.querySelectorAll('select') || []).filter(
          (select) => !select.disabled
        );

        return selectInputs.every((select) => {
          const selectedOption = select.selectedOptions[0];
          return selectedOption && !selectedOption.classList.contains('mrk-unchosen') && selectedOption.value !== '';
        });
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products'
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list'
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}
